import { CdkConnectedOverlay, OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  DestroyRef,
  EventEmitter,
  forwardRef,
  HostListener,
  inject,
  Input,
  Output,
  QueryList,
  signal,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ClickedOutsideDirective, TemplateWithValueDirective } from '@coin/shared/core/util-directives';
import { DefaultControlValueAccessor, GlobalEventsService } from '@coin/shared/core/util-helpers';
import { TranslateModule } from '@ngx-translate/core';
import { animationFrames, filter, first, switchMap } from 'rxjs';
import { V2TextInputComponent } from '../text-input/v2-text-input.component';

// TODO: needs Result open animation
@Component({
  selector: 'coin-v2-search-box',
  templateUrl: './v2-search-box.component.html',
  styleUrls: ['./v2-search-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    FormsModule,
    TranslateModule,
    V2TextInputComponent,
    OverlayModule,
    ReactiveFormsModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => V2SearchBoxComponent),
      multi: true
    }
  ],
  hostDirectives: [
    {
      directive: ClickedOutsideDirective,
      outputs: ['clickedOutside']
    }
  ]
})
export class V2SearchBoxComponent<T = unknown> extends DefaultControlValueAccessor<T> implements AfterViewInit {
  private destroy = inject(DestroyRef);
  private events = inject(GlobalEventsService);

  @Input() placeholder: string;
  @Input() label: string;
  @Input() icon = 'search';
  @Input() loading: boolean;
  @Input() noResultsTextKey = 'general.no-results-found';
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() search = new EventEmitter<string>();
  @ContentChildren(TemplateWithValueDirective) results: QueryList<TemplateWithValueDirective<T>>;

  @ViewChild(V2TextInputComponent) searchInputElement: V2TextInputComponent;
  @ViewChild(CdkConnectedOverlay) cdkOverlay: CdkConnectedOverlay;

  protected isSearchResultVisible = signal<boolean>(false);
  protected searchControl = new FormControl('');

  @HostListener('focusin')
  public onFocusEnter(): void {
    if (!this.isSearchResultVisible()) {
      this.isSearchResultVisible.set(true);
    }
  }

  @HostListener('clickedOutside')
  public handleClickedOutside() {
    if (this.searchControl.value) {
      this.isSearchResultVisible.set(false);
    }
  }

  public onEscape(): void {
    if (this.isSearchResultVisible()) {
      this.isSearchResultVisible.set(false);
      this.searchInputElement.blur();
    }
  }

  constructor() {
    super();
    this.events
      .listen('keydown')
      .pipe(takeUntilDestroyed())
      .subscribe(event => {
        if (event.key === 'Escape') {
          this.onEscape();
        }
      });
  }

  public ngAfterViewInit(): void {
    this.results.changes
      .pipe(
        switchMap(() => animationFrames().pipe(first())),
        takeUntilDestroyed(this.destroy)
      )
      .subscribe(() => {
        this.cd.markForCheck();
        this.cdkOverlay.overlayRef?.updatePosition();
      });

    this.searchControl.valueChanges
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroy))
      .subscribe(value => this.search.emit(value));
  }

  protected selectResult(value: T): void {
    this.isSearchResultVisible.set(false);

    this.value = value;
    this.resetSearch();
  }

  protected clearValue(): void {
    this.value = undefined;
    this.resetSearch();
  }

  private resetSearch(): void {
    this.searchControl.reset('', { emitEvent: false });
  }

  protected handleDetach(): void {
    if (this.value) {
      this.resetSearch();
    }
  }

  public override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);
    isDisabled ? this.searchControl.disable({ emitEvent: false }) : this.searchControl.enable({ emitEvent: false });
  }
}
